import React, {ChangeEvent, Component, ReactNode} from 'react';
import {FormControl, FormHelperText, InputLabel, MenuProps, Select} from '@material-ui/core';
import './index.scss';
import {ValidatedFieldProps, ValidatedFieldState} from 'components/forms/validation';

export interface SelectFieldProps extends ValidatedFieldProps {
  label?: string;
  name?: string;
  disabled?: boolean;
  onChange?: (value:string)=>void;
  className?: string;
  defaultValue?: any;
  value?: any;
  placeholder?: string;
  startAdornment?: ReactNode,
  endAdornment?: ReactNode,
  menuProps?: Partial<MenuProps>;
  parseValue?: (val: string)=>any;
  formatValue?: (val: any)=>string;
}

interface SelectFieldState extends ValidatedFieldState {
  labelWidth: number;
  value: string|undefined;
}

export default class SelectField extends Component<SelectFieldProps, SelectFieldState> {
  private inputLabel = React.createRef<HTMLLabelElement>();

  constructor(props: SelectFieldProps) {
    super(props);
    this.state = {
      labelWidth: 0,
      dirty: false,
      touched: false,
      validationMessage: undefined,
      value: undefined
    };
  }

  componentDidMount(): void {
    const current: any = this.inputLabel.current;
    if(current) {
      this.setState({ labelWidth: current.offsetWidth });
    }
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.addFieldListener(this.props.name, this.onValidChanged);
    }
    const val = this.props.value !== undefined ? this.props.value : this.props.defaultValue;
    if(val !== undefined) {
      this.setState({value: val});
    }
  }

  componentDidUpdate(prevProps: Readonly<SelectFieldProps>, prevState: Readonly<SelectFieldState>, snapshot?: any): void {
    if(this.props.value !== undefined && this.props.value !== prevProps.value) {
      this.setState({value: this.props.value});
    } else if(this.props.defaultValue !== undefined && this.state.value === undefined) {
      this.setState({value: this.props.defaultValue});
    }
  }

  componentWillUnmount(): void {
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.removeFieldListener(this.props.name, this.onValidChanged);
    }
  }

  onValidChanged = (errors: string[], forceDirty: boolean) => {
    const touched = this.state.touched || forceDirty;
    const dirty = this.state.dirty || forceDirty;
    if(errors) {
      let msg = this.props.validationMessages ? this.props.validationMessages[errors[0]] : null;
      if(!msg) {
        msg = errors[0];
      }
      this.setState({ validationMessage: msg, dirty: dirty, touched: touched });
    } else {
      this.setState({ validationMessage: undefined, dirty: dirty, touched: touched });
    }
  };

  onChange = (ev: ChangeEvent<any>) => {
    let value = (ev.target as HTMLSelectElement).value;
    if(this.props.parseValue) {
      value = this.props.parseValue(value);
    }
    this.setState({dirty: true, touched: true, value: value}, () => {
      if(this.props.model && this.props.name) {
        this.props.model[this.props.name] = value;
      }
      if(this.props.validationGroup && this.props.name) {
        this.props.validationGroup.validateField(this.props.name);
      }
      if(this.props.onChange) {
        this.props.onChange(value);
      }
    });
  };

  onRenderValue = () => {
    return this.props.placeholder;
  };

  render() {
    const showError = this.state.touched && this.state.dirty && !!this.state.validationMessage;
    const fullClassName = this.props.className ? `select-field ${this.props.className}` : 'select-field';
    let value = this.state.value !== undefined && this.state.value !== null ? this.state.value : undefined;
    if(value === undefined && this.props.model && this.props.name) {
      value = this.props.model[this.props.name];
    }
    if(!value) {
      value = '';
    } else if(this.props.formatValue) {
      value = this.props.formatValue(value);
    }
    const showPlaceholder = !!this.props.placeholder && value === '';
    return <FormControl variant="outlined" className={fullClassName} error={showError} disabled={this.props.disabled}>
        <InputLabel ref={this.inputLabel} id="select-label">
          { this.props.label }
        </InputLabel>
        <Select labelWidth={this.state.labelWidth} labelId="select-label" onChange={this.onChange}
                renderValue={showPlaceholder ? this.onRenderValue : undefined} displayEmpty={showPlaceholder}
                value={value} startAdornment={this.props.startAdornment} endAdornment={this.props.endAdornment}
                MenuProps={this.props.menuProps}>
          { this.props.children }
        </Select>
        { showError && <FormHelperText>{ this.state.validationMessage }</FormHelperText> }
      </FormControl>;
  }
}